import { Injectable } from '@angular/core';
import { SiteFormResponse } from 'app/_models/site-forms/site-form-response.model';
import { environment } from 'environments/environment';
import { jsPDF } from "jspdf";
import autoTable, { Table } from 'jspdf-autotable';

@Injectable()
export class PdfExportService {
  private xCoordinate = 0;
  private yCoordinate = 0;
  private doc: jsPDF;

  // all units are pt
  private xMargin = 20;
  private yMargin = 20;
  // font sizes
  private footerFontSize = 11;
  private defaultFontSize = 12;
  private smallHeaderFontSize = 14;
  private largetHeaderFontSize = 18;

  // extra spacing
  private yLineSpacing = 6; // increase vertical distance between lines of text
  private gapBetweenQuestionAndAnswer = 30;
  private headerMarginTop = 24;
  private headerMarginBottom = 3;

  // A4 Size - 210 x 297 mm / 8.27 x 11.60 inches / 595 x 842 pt
  private a4Width = 595;
  private a4Height = 842;
  private a4WidthMinusMargins = this.a4Width - this.xMargin - this.xMargin;
  private a4HeightMinusMargins = this.a4Height - this.yMargin - this.yMargin - this.footerFontSize;

  private footerYCoordinate = this.a4Height - this.yMargin - this.footerFontSize;

  // Header logo images are created as 360 x 247 _px_, but displayed as 120 x 82.33 _pt_
  private imageWidth = 120;
  private imageHeightSpace = 83;

  // different margins between headers and non-headers
  private lastThingAddedWasHeader = false;

  public createPdfFromSiteFormResponse(siteFormResponse: SiteFormResponse, logoUrl: string, includePhotos: boolean) {
    this.doc = new jsPDF('p', 'pt', 'a4');
    this.setDefaultFont();

    this.xCoordinate = this.xMargin;
    this.yCoordinate = this.yMargin;

    // logo in header
    this.doc.addImage(logoUrl, 'PNG', this.a4Width - this.imageWidth - this.xMargin, this.yCoordinate, this.imageWidth, 0); // 0 for auto height
    this.increaseYCoordinate(this.imageHeightSpace);

    // all the form data
    for (let i = 0; i < siteFormResponse.form.data.length; i++) {
      const field = siteFormResponse.form.data[i];

      if (field.type === 'headerLarge') {
        this.addHeaderLarge(field);
      }
      if (field.type === 'headerSmall') {
        this.addHeaderSmall(field);
      }
      if (field.type === 'textField' || field.type === 'textArea' || field.type === 'dateField' || field.type === 'timeField' || field.type === 'radioButton' || field.type === 'checkboxList') {
        this.addInputAndAnswer(field, siteFormResponse.getAnswer(i));
      }
      if (field.type === 'imageUpload') {
        this.addInputAndAnswer(field, siteFormResponse.getAnswer(i), includePhotos);
      }
      if (field.type === 'documentUpload') {
        this.addInputAndAnswer(field, siteFormResponse.getAnswer(i));
        this.addLinkToPortal(siteFormResponse);
      }
      if (field.type === 'table') {
        this.addTable(i);
      }
    }

    // footer metadata
    this.setFootersOnAllPages(siteFormResponse);

    // download
    const fileName = `${siteFormResponse.form.name} ${siteFormResponse.createdDate.toISOString()}.pdf`;
    this.doc.save(fileName);
  }

  private addLinkToPortal(siteFormResponse: SiteFormResponse) {
    const message = `See documents at:`;
    const url = `${environment.sourceUrl}/site-owner/site-forms/${siteFormResponse.form.id}/response/${siteFormResponse.id}`;

    this.doc.text(message, this.xCoordinate, this.yCoordinate + this.yLineSpacing);
    this.increaseYCoordinate(this.defaultFontSize + this.yLineSpacing);
    this.doc.text(url, this.xCoordinate, this.yCoordinate + this.yLineSpacing);
    this.increaseYCoordinate(this.defaultFontSize + this.yLineSpacing);
  }

  private setDefaultFont() {
    this.doc.setFont('times', 'normal');
    this.doc.setFontSize(this.defaultFontSize);
  }

  private setDefaultBoldFont() {
    this.doc.setFont('times', 'bold');
    this.doc.setFontSize(this.defaultFontSize);
  }

  private addHeaderLarge(field) {
    this.doc.setFont('times', 'bold');
    this.doc.setFontSize(this.largetHeaderFontSize);

    if (!this.lastThingAddedWasHeader) {
      this.increaseYCoordinate(this.headerMarginTop);
    }
    this.lastThingAddedWasHeader = true;

    this.doc.text(field.text, this.xCoordinate, this.yCoordinate + this.yLineSpacing)
    this.increaseYCoordinate(this.largetHeaderFontSize + this.yLineSpacing);
  }

  private addHeaderSmall(field) {
    this.doc.setFont('times', 'bold');
    this.doc.setFontSize(this.smallHeaderFontSize);

    if (!this.lastThingAddedWasHeader) {
      this.increaseYCoordinate(this.headerMarginTop);
    }
    this.lastThingAddedWasHeader = true;

    this.doc.text(field.text, this.xCoordinate, this.yCoordinate + this.yLineSpacing)
    this.increaseYCoordinate(this.smallHeaderFontSize + this.yLineSpacing);
  }

  private addInputAndAnswer(field, answer = '', isPhoto = false) {
    //
    // QUESTION
    //
    if (this.lastThingAddedWasHeader) {
      this.increaseYCoordinate(this.headerMarginBottom);
    }
    this.lastThingAddedWasHeader = false;

    this.setDefaultFont();

    const questionText = field.label;
    let lastLineOfQuestionLength = 0;

    // split the question if its too long, so it wraps
    const textArray1 = this.doc.splitTextToSize(questionText, this.a4WidthMinusMargins);
    textArray1.forEach(textPart => {
      this.doc.text(textPart.trim(), this.xCoordinate, this.yCoordinate + this.yLineSpacing);
      this.yCoordinate += this.defaultFontSize + this.yLineSpacing; // don't use this.increaseYCoordinate() because dont want to go to a new page

      lastLineOfQuestionLength = this.doc.getTextWidth(textPart);
    });

    // if its multiline question use a negative offset for the gap, answer will be on new line without gap
    if (textArray1.length > 1) {
      lastLineOfQuestionLength = (- this.gapBetweenQuestionAndAnswer);
      this.increaseYCoordinate(0) // now add new page if necessary
    } else {
      // if only single line question, bring the y up again, so the answer is in line with question
      this.yCoordinate -= this.defaultFontSize;
      this.yCoordinate -= this.yLineSpacing;
    }

    //
    // ANSWER
    //

    this.setDefaultBoldFont();

    const boldText = answer;
    const xOffset = this.gapBetweenQuestionAndAnswer + lastLineOfQuestionLength;
    // do just the first line of the answer - it may be in line with question or on a newline
    const textArray2 = this.doc.splitTextToSize(boldText, this.a4WidthMinusMargins - xOffset);
    this.doc.text(textArray2[0].trim(), this.xCoordinate + xOffset, this.yCoordinate + this.yLineSpacing);
    this.increaseYCoordinate(this.defaultFontSize + this.yLineSpacing);

    // then do the remaining lines of the answer, if any
    const remainderText = answer.replace(textArray2[0], '');
    if (remainderText.length > 0) {
      let textArray3 = this.doc.splitTextToSize(remainderText, this.a4WidthMinusMargins);
      textArray3.forEach(textPart => {
        this.doc.text(textPart.trim(), this.xCoordinate, this.yCoordinate + this.yLineSpacing);
        this.increaseYCoordinate(this.defaultFontSize + this.yLineSpacing);
      });
    }

    this.setDefaultFont();

    if (isPhoto) {
      this.addPhotoGrid(field);
    }
  }

  private addPhotoGrid(field: any) {
    const numberOfImagesPerRow = 3;
    const maxImageHeight = 300;
    const imageMargin = 10;
    const totalMarginWidth = (numberOfImagesPerRow - 1) * imageMargin;
    const totalAvailableWidth = this.a4WidthMinusMargins - totalMarginWidth;
    const imageWidth = totalAvailableWidth / numberOfImagesPerRow;

    let photoRowHeight = 0;

    field.objectUrls.forEach((objectUrl, i) => {
      const imageIndex = (i % numberOfImagesPerRow) + 1; // Calculate the image index per row (e.g. 1, 2, 3, repeat)

      // Create an Image object to help determine heights / aspect ratio
      const img = new Image();
      img.src = objectUrl;
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      let adjustedImageWidth = imageWidth;
      let adjustedImageHeight = imageWidth / aspectRatio;

      // Check if adjusting the height is necessary
      if (adjustedImageHeight > maxImageHeight) {
          adjustedImageHeight = maxImageHeight;
          adjustedImageWidth = maxImageHeight * aspectRatio;
      }

      photoRowHeight = Math.max(adjustedImageHeight, photoRowHeight);

      const imageX = this.xCoordinate + ((imageIndex - 1) * (imageWidth + imageMargin));

      // Check if adding this photo will exceed the page height
      if (this.yCoordinate + photoRowHeight > this.a4HeightMinusMargins) {
        this.doc.addPage();
        this.doc.setPage(this.doc.getCurrentPageInfo().pageNumber);
        this.yCoordinate = this.yMargin;
      }

      this.doc.addImage(objectUrl, 'PNG', imageX, this.yCoordinate, adjustedImageWidth, adjustedImageHeight);

      if (imageIndex === numberOfImagesPerRow) {
        this.increaseYCoordinate(photoRowHeight + imageMargin);
        photoRowHeight = 0;
      }
    });

    this.increaseYCoordinate(photoRowHeight + this.yLineSpacing);
  }

  private addTable(i: number) {
    let tableMeta: Table;

    if (!this.lastThingAddedWasHeader) {
      this.increaseYCoordinate(this.headerMarginBottom);
    }
    this.lastThingAddedWasHeader = false;

    // add table from the html by id/index
    autoTable(this.doc, {
      html: '#my-table-' + i,
      theme: 'grid',
      startY: this.yCoordinate,
      showHead: 'firstPage',
      styles: {
        font: 'times'
      },
      headStyles: {
        textColor: [80, 80, 80], // black
        fillColor: [256, 256, 256], // white
        lineColor: [213, 213, 213], // grey
        lineWidth: 0.1,
      },
      didParseCell: (data) => {
        tableMeta = data.table; // capture for the height offset
      },
    });

    this.yCoordinate = tableMeta.finalY;
    this.increaseYCoordinate(this.defaultFontSize + this.yLineSpacing);
  }

  private setFootersOnAllPages(siteFormResponse: SiteFormResponse) {
    this.doc.setFontSize(this.footerFontSize);
    this.doc.setTextColor('#A9A9A9'); // gray

    const totalPages = this.doc.getNumberOfPages();
    for (let p = 0; p < totalPages; p++) {
      this.doc.setPage(p + 1);
      this.addFooter(siteFormResponse, totalPages);
    }

    this.setDefaultFont();
  }

  private addFooter(siteFormResponse: SiteFormResponse, totalPages) {
    // add work order string if present
    let wStr = '';
    if (siteFormResponse.workOrder) {
      wStr = ' - WRK' + siteFormResponse.workOrder.id;
    }
    let completedBy = siteFormResponse.user.fullName;
    if (siteFormResponse.submittedBy) {
      completedBy += ` (Submitted By: ${siteFormResponse.submittedBy})`
    }


    // person - site - work order - at date - Page x/y
    const footer = `${completedBy} - ${siteFormResponse.site.name}${wStr} - at ${siteFormResponse.createdDate.toLocaleString()} - Page ${this.doc.getCurrentPageInfo().pageNumber}/${totalPages}`;

    // wrap text if too long
    const textArray = this.doc.splitTextToSize(footer, this.a4WidthMinusMargins);
    let y = this.footerYCoordinate;
    textArray.forEach(textPart => {
      this.doc.text(textPart.trim(), this.a4WidthMinusMargins / 2, y, { align: 'center' });
      y += this.footerFontSize;
    });
  }

  private increaseYCoordinate(addValue) {
    this.yCoordinate += addValue;

    if (this.yCoordinate > this.a4HeightMinusMargins) {
      this.doc.addPage();
      this.doc.setPage(this.doc.getCurrentPageInfo().pageNumber);
      this.yCoordinate = this.yMargin;
    }
  }
}
